/**
 * Reports API contracts (project brief §21/§30, `GET /reports/*`).
 *
 * The response shape is deliberately TIERED rather than one flat object with
 * every field always present: `GET /reports/summary` returns only the tiers
 * the caller's permissions actually unlock (`user` always; `technician` when
 * the actor holds team-scoped ticket visibility; `manager` when the actor
 * holds `report:read:team`/`report:read:any`; `admin` when the actor holds
 * `report:read:any`). A field's mere presence in the response is therefore
 * itself meaningful, and the frontend renders sections based on which keys
 * came back rather than the actor's role name — the same "ask the server what
 * it actually granted" discipline `SessionResponse.permissions` documents.
 *
 * Every number in every tier is produced by a query ANDed against the same
 * `TicketVisibilitySpec` the ticket list/detail endpoints use
 * (`apps/api/src/modules/reports/reports.service.ts` reuses
 * `TicketRepository.visibilityWhere`/`countVisible` — there is no second
 * authorization path here).
 */
import { z } from 'zod';
import { TICKET_STATUSES } from './ticket-state-machine.js';
// ---------------------------------------------------------------------------
// Query DTOs
// ---------------------------------------------------------------------------
/** Shared date-range query, used by `/reports/trends` and `/reports/sla`. Bounded to <=92 days so an aggregate query can never be asked to scan an unbounded window. */
export const reportDateRangeQueryDtoSchema = z
    .object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    teamId: z.string().uuid().optional(),
})
    .refine((v) => !v.from || !v.to || v.from.getTime() <= v.to.getTime(), {
    message: 'from must be before to',
    path: ['from'],
})
    .refine((v) => !v.from || !v.to || v.to.getTime() - v.from.getTime() <= 92 * 24 * 60 * 60 * 1000, {
    message: 'the range may not exceed 92 days',
    path: ['to'],
});
export const REPORT_TREND_BUCKETS = ['day', 'week'];
/** True when `tz` is an IANA time zone this runtime knows (e.g. "Asia/Kolkata"). */
export function isValidTimeZone(tz) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
export const reportTrendsQueryDtoSchema = reportDateRangeQueryDtoSchema.and(z.object({
    bucket: z.enum(REPORT_TREND_BUCKETS).default('day'),
    /**
     * IANA time zone the day/week buckets are cut in (the viewer's own zone),
     * so "today" means the viewer's calendar day. Omitted = UTC.
     */
    tz: z.string().trim().min(1).max(64).refine(isValidTimeZone, { message: 'tz must be an IANA time zone' }).optional(),
}));
// ---------------------------------------------------------------------------
// Response DTOs
// ---------------------------------------------------------------------------
export const reportUserSummarySchema = z.object({
    myOpen: z.number().int().nonnegative(),
    awaitingMyConfirmation: z.number().int().nonnegative(),
    resolvedAwaitingUser: z.number().int().nonnegative(),
    closed: z.number().int().nonnegative(),
});
export const reportTechnicianSummarySchema = z.object({
    assignedToMe: z.number().int().nonnegative(),
    unassignedInMyTeams: z.number().int().nonnegative(),
    inProgress: z.number().int().nonnegative(),
    pendingUser: z.number().int().nonnegative(),
    slaAtRisk: z.number().int().nonnegative(),
    slaBreached: z.number().int().nonnegative(),
    reopened: z.number().int().nonnegative(),
});
export const AGING_BUCKETS = ['0-1d', '1-3d', '3-7d', '7d+'];
export const reportAgingSchema = z.record(z.enum(AGING_BUCKETS), z.number().int().nonnegative());
export const reportTeamWorkloadSchema = z.object({
    teamId: z.string().uuid(),
    teamName: z.string(),
    open: z.number().int().nonnegative(),
    unassigned: z.number().int().nonnegative(),
    slaAtRisk: z.number().int().nonnegative(),
    slaBreached: z.number().int().nonnegative(),
    escalated: z.number().int().nonnegative(),
});
export const reportManagerSummarySchema = z.object({
    /** Per-team rows, restricted to teams the actor may report on (their led teams, or every org team for `report:read:any`). */
    teamWorkload: z.array(reportTeamWorkloadSchema),
    /** Percentage (0-100, one decimal) of tickets RESOLVED or CLOSED within `resolutionDueAt`, over the trailing 30 days. `null` when there is no SLA-eligible sample to divide by. */
    slaComplianceRate: z.number().min(0).max(100).nullable(),
    escalations: z.number().int().nonnegative(),
    aging: reportAgingSchema,
    /** Median minutes from `createdAt` to `resolvedAt`, trailing 30 days. `null` with no resolved sample. */
    mttrMinutes: z.number().nonnegative().nullable(),
    /** Median minutes from `createdAt` to `firstResponseAt`, trailing 30 days. `null` with no sample. */
    firstResponseMedianMinutes: z.number().nonnegative().nullable(),
    /** Share (0-100) of tickets created in the trailing 30 days that were reopened at least once. */
    reopenRatePercent: z.number().min(0).max(100).nullable(),
    resolutionTrend: z.array(z.object({ date: z.string(), resolved: z.number().int().nonnegative() })),
});
export const reportSystemHealthSchema = z.object({
    outboxBacklog: z.number().int().nonnegative(),
    outboxFailed: z.number().int().nonnegative(),
    emailNeedsReview: z.number().int().nonnegative(),
    emailQuarantined: z.number().int().nonnegative(),
});
export const reportOrgSummarySchema = z.object({
    totalOpen: z.number().int().nonnegative(),
    totalBreached: z.number().int().nonnegative(),
    teamWorkload: z.array(reportTeamWorkloadSchema),
    systemHealth: reportSystemHealthSchema,
});
/**
 * `GET /reports/summary` response. Only tiers the actor's permissions unlock
 * are populated; the rest are omitted (never sent as zeroed placeholders,
 * which would look like real data with nothing in it).
 */
export const reportSummaryDtoSchema = z.object({
    user: reportUserSummarySchema,
    technician: reportTechnicianSummarySchema.optional(),
    manager: reportManagerSummarySchema.optional(),
    admin: reportOrgSummarySchema.optional(),
});
export const reportTrendPointSchema = z.object({ date: z.string(), created: z.number().int().nonnegative(), resolved: z.number().int().nonnegative(), closed: z.number().int().nonnegative() });
export const reportTrendsDtoSchema = z.object({ bucket: z.enum(REPORT_TREND_BUCKETS), points: z.array(reportTrendPointSchema) });
/**
 * Mean elapsed time for one lifecycle interval over the report window, plus
 * the same figure for the equally long window immediately before it (for a
 * "↓ 28%" style comparison). Means are arithmetic means of real samples; a
 * figure is `null` when its window has no sample (never 0 as a placeholder).
 */
export const reportDurationStatSchema = z.object({
    meanMinutes: z.number().nonnegative().nullable(),
    sampleSize: z.number().int().nonnegative(),
    previousMeanMinutes: z.number().nonnegative().nullable(),
    previousSampleSize: z.number().int().nonnegative(),
});
export const reportSlaDtoSchema = z.object({
    from: z.string(),
    to: z.string(),
    onTrack: z.number().int().nonnegative(),
    atRisk: z.number().int().nonnegative(),
    breached: z.number().int().nonnegative(),
    met: z.number().int().nonnegative(),
    complianceRate: z.number().min(0).max(100).nullable(),
    /**
     * `firstResponse`: `createdAt` → `firstResponseAt`, for tickets whose first
     * response happened inside the window. `resolution`: `createdAt` →
     * `resolvedAt`, for tickets resolved inside the window. Same visibility
     * scope and team filter as the SLA counts.
     */
    firstResponse: reportDurationStatSchema,
    resolution: reportDurationStatSchema,
    byTeam: z.array(z.object({
        teamId: z.string().uuid(),
        teamName: z.string(),
        onTrack: z.number().int().nonnegative(),
        atRisk: z.number().int().nonnegative(),
        breached: z.number().int().nonnegative(),
        met: z.number().int().nonnegative(),
        complianceRate: z.number().min(0).max(100).nullable(),
    })),
});
/**
 * `GET /reports/status-changes` — net change per status over the last 24
 * hours, derived from the database-written `ticket_status_history` (every
 * creation and status change, including a ticket's initial status), for the
 * tickets the caller can currently see. `net[S]` = entries into S − exits
 * from S in the window, which is exactly "count now − count 24 h ago" for
 * that visible set. Statuses with no movement are reported as 0.
 */
export const reportStatusChangesDtoSchema = z.object({
    since: z.string(),
    until: z.string(),
    net: z.record(z.enum(TICKET_STATUSES), z.number().int()),
});
// ---------------------------------------------------------------------------
// Report export — GET /reports/export?format=&scope=&tz=
//   ADMIN (report:read:any): scope=org, format=xlsx only.
//   TEAM_LEAD (report:read:team): scope=team (teams the actor leads), xlsx|pdf.
// Scope is enforced server-side; the response is a file download.
// ---------------------------------------------------------------------------
export const REPORT_EXPORT_FORMATS = ['xlsx', 'pdf'];
export const REPORT_EXPORT_SCOPES = ['org', 'team'];
export const reportExportQuerySchema = z.object({
    format: z.enum(REPORT_EXPORT_FORMATS),
    scope: z.enum(REPORT_EXPORT_SCOPES),
    /** IANA zone used for every timestamp in the file (validated server-side). */
    tz: z.string().trim().min(1).max(64).refine(isValidTimeZone, { message: 'tz must be an IANA time zone' }).optional(),
});
//# sourceMappingURL=report-dto.js.map